import { useEffect, useState, useCallback } from 'react';

import { supabase } from '../../shared/services/supabaseService';

export interface FriendsInvitation {
  id: string;
  group_id: string;
  group_name: string;
  invited_by: string;
  status: string;
  created_at: string;
  sender_name?: string;
}

export function useFriendsInvitations(userId: string | null) {
  const [pendingInvites, setPendingInvites] = useState<FriendsInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    if (!userId) {
      setPendingInvites([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('get_pending_friends_invitations_for_user', {
        uid: userId,
      });
      if (error) throw error;
      setPendingInvites(data || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load invitations';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const respondToInvite = async (invitationId: string, accept: boolean) => {
    const { error } = await supabase.rpc('respond_to_friends_invite', {
      _invitation_id: invitationId,
      _accept: accept,
    });
    if (error) throw error;
    // refresh list on success
    await fetchInvites();
  };

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  return {
    pendingInvites,
    loading,
    error,
    refetch: fetchInvites,
    acceptInvite: (id: string) => respondToInvite(id, true),
    declineInvite: (id: string) => respondToInvite(id, false),
  };
}
